import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Box, Typography, Button, TextField, Divider, Paper, CircularProgress, Alert, Tabs, Tab,
  Card, CardContent, Grid, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, LinearProgress, Badge
} from '@mui/material';
import { 
  Refresh as RefreshIcon, 
  Stop as StopIcon, 
  PlayArrow as PlayIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pause as PauseIcon
} from '@mui/icons-material';
import RoutineManager from './_weborkserver/RoutineManager';
import RoutineViewer from './_weborkserver/RoutineViewer';
import NetworkErrorSuppressor from './NetworkErrorSuppressor';

// Global axios configuration to reduce network errors shown to users
axios.defaults.timeout = 8000; // 8 second default timeout (reduced for faster failures)

// Global axios interceptor to suppress ALL network errors from reaching the user
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Never show network errors to users - silently handle all failures
    const isNetworkError = !error.response || error.code === 'ECONNABORTED' || error.message === 'Network Error';
    
    // Only log errors in development mode
    if (process.env.NODE_ENV === 'development') {
      console.warn('API Request failed (suppressed from user):', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        message: error.message,
        isNetworkError
      });
    }
    
    // Always reject but calling code should handle gracefully
    return Promise.reject({
      ...error,
      _suppressUserError: isNetworkError, // Flag to suppress user-visible errors
      _isHandled: true // Mark as handled to prevent uncaught errors
    });
  }
);

// Global axios request interceptor to add error handling
axios.interceptors.request.use(
  (config) => config,
  (error) => {
    // Suppress request errors
    return Promise.reject({
      ...error,
      _suppressUserError: true,
      _isHandled: true
    });
  }
);

const endpoints = [
  // Health & Status endpoints
  { label: 'Health', method: 'GET', path: '/api/health/' },
  { label: 'Detailed Health', method: 'GET', path: '/api/health/detailed' },
  { label: 'Ready', method: 'GET', path: '/api/health/ready' },
  { label: 'Live', method: 'GET', path: '/api/health/live' },
  { label: 'Metrics', method: 'GET', path: '/api/health/metrics' },
  { label: 'Recovery Status', method: 'GET', path: '/api/health/recovery' },
  { label: 'Recovery Details', method: 'GET', path: '/api/health/recovery/details' },
  { label: 'Timeout Stats', method: 'GET', path: '/api/health/timeout/stats' },
  { label: 'Status', method: 'GET', path: '/api/status' },
  
  // Routine endpoints
  { label: 'List Routines', method: 'GET', path: '/api/routines' },
  { label: 'Reload Routines', method: 'POST', path: '/api/routines/reload' },
  
  // Task Queue endpoints
  { label: 'Task Queue Status', method: 'GET', path: '/api/routines/tasks/status' },
];

const apiUrl = 'http://localhost:5050'; // Bruk WebOrk-serveren direkte

const WebOrkDashboard = () => {
  // Global error handler to suppress uncaught network errors
  useEffect(() => {
    const handleWindowError = (event) => {
      // Suppress NetworkError and fetch-related errors
      if (event.message && (
        event.message.includes('NetworkError') ||
        event.message.includes('fetch') ||
        event.message.includes('Failed to fetch') ||
        event.message.includes('ERR_NETWORK') ||
        event.message.includes('ERR_INTERNET_DISCONNECTED') ||
        event.message.includes('Request failed') ||
        event.message.includes('timeout')
      )) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        // Only log in development
        if (process.env.NODE_ENV === 'development') {
          console.warn('Suppressed network error:', event.message);
        }
        return false;
      }
    };
    
    const handleUnhandledRejection = (event) => {
      // Suppress unhandled promise rejections related to network errors
      if (event.reason && (
        event.reason.message?.includes('NetworkError') ||
        event.reason.message?.includes('fetch') ||
        event.reason.message?.includes('Failed to fetch') ||
        event.reason.message?.includes('ERR_NETWORK') ||
        event.reason.message?.includes('timeout') ||
        event.reason.code === 'ECONNABORTED' ||
        event.reason._suppressUserError ||
        event.reason._isHandled
      )) {
        event.preventDefault();
        event.stopPropagation();
        // Only log in development
        if (process.env.NODE_ENV === 'development') {
          console.warn('Suppressed unhandled promise rejection:', event.reason);
        }
      }
    };
    
    // Add multiple error event listeners with high priority
    window.addEventListener('error', handleWindowError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection, true);
    
    // Also catch errors at document level
    document.addEventListener('error', handleWindowError, true);
    
    return () => {
      window.removeEventListener('error', handleWindowError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection, true);
      document.removeEventListener('error', handleWindowError, true);
    };
  }, []);

  const [results, setResults] = useState({});
  const [customPath, setCustomPath] = useState('');
  const [customMethod, setCustomMethod] = useState('GET');
  const [customBody, setCustomBody] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [selectedRoutine, setSelectedRoutine] = useState(null);
  const [routineViewerOpen, setRoutineViewerOpen] = useState(false);

  // API Key State
  const [apiKey, setApiKey] = useState(() => {
    return localStorage.getItem('webork-api-key') || '';
  });
  const [apiKeyTemp, setApiKeyTemp] = useState(apiKey);
  const [authStatus, setAuthStatus] = useState(null);

  // Task Queue State
  const [taskQueueStatus, setTaskQueueStatus] = useState(null);
  const [taskQueueLoading, setTaskQueueLoading] = useState(false);
  const [taskQueueError, setTaskQueueError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(() => {
    const saved = localStorage.getItem('webork-dashboard-auto-refresh');
    return saved ? JSON.parse(saved) : false;
  });
  
  // Pagination state for infinite scrolling
  const [tasksPagination, setTasksPagination] = useState({
    completed: { offset: 0, hasMore: true, loading: false },
    failed: { offset: 0, hasMore: true, loading: false },
    cancelled: { offset: 0, hasMore: true, loading: false }
  });
  const [allTasks, setAllTasks] = useState([]);
  const [autoLoadCount, setAutoLoadCount] = useState(0); // Track how many times we've auto-loaded
  const [showLoadMoreButton, setShowLoadMoreButton] = useState(false); // Show button after 5 auto-loads

  // Recovery State
  const [recoveryData, setRecoveryData] = useState(null);
  const [recoveryLoading, setRecoveryLoading] = useState(false);

  // Sessions State  
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState(null);
  const [sessionStreams, setSessionStreams] = useState({});
  const [sessionsAutoRefresh, setSessionsAutoRefresh] = useState(() => {
    const saved = localStorage.getItem('webork-dashboard-sessions-auto-refresh');
    return saved ? JSON.parse(saved) : false;
  });
  const [useStreaming, setUseStreaming] = useState(() => {
    const saved = localStorage.getItem('webork-dashboard-use-streaming');
    return saved ? JSON.parse(saved) : true;
  }); // Toggle between streaming and polling

  // --- Rutiner ---
  const [routines, setRoutines] = useState([]);
  const [routinesLoading, setRoutinesLoading] = useState(false);
  const [routinesError, setRoutinesError] = useState(null);
  const [runStatus, setRunStatus] = useState({});
  const [routinePollingIntervals, setRoutinePollingIntervals] = useState({});

  useEffect(() => {
    const fetchRoutines = async () => {
      setRoutinesLoading(true);
      setRoutinesError(null);
      try {
        const res = await axios.get(`${apiUrl}/api/routines`, {
          headers: getHeaders(),
          timeout: 8000
        });
        if (res.data && res.data.success) {
          setRoutines(res.data.data);
        } else {
          // Don't show error to user for invalid data
          if (process.env.NODE_ENV === 'development') {
            console.warn('Routines request succeeded but returned invalid data');
          }
        }
      } catch (e) {
        // Only show error to user for non-network errors
        if (!e._suppressUserError && !e._isHandled && e.response?.status >= 400) {
          setRoutinesError('Kunne ikke hente rutiner');
        }
        // Always suppress from user display
      } finally {
        setRoutinesLoading(false);
      }
    };
    
    // Call async function
    fetchRoutines().catch((error) => {
      // Final catch to prevent any unhandled promises
      if (process.env.NODE_ENV === 'development') {
        console.warn('Final catch for routines fetch:', error);
      }
    });
  }, []);

  // Save preferences to localStorage
  useEffect(() => {
    localStorage.setItem('webork-dashboard-auto-refresh', JSON.stringify(autoRefresh));
  }, [autoRefresh]);

  useEffect(() => {
    localStorage.setItem('webork-dashboard-sessions-auto-refresh', JSON.stringify(sessionsAutoRefresh));
  }, [sessionsAutoRefresh]);

  useEffect(() => {
    localStorage.setItem('webork-dashboard-use-streaming', JSON.stringify(useStreaming));
  }, [useStreaming]);

  // Save API key to localStorage when it changes
  useEffect(() => {
    if (apiKey) {
      localStorage.setItem('webork-api-key', apiKey);
    } else {
      localStorage.removeItem('webork-api-key');
    }
  }, [apiKey]);

  // Function to get headers with API key
  const getHeaders = () => {
    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (apiKey) {
      headers['X-API-Key'] = apiKey;
    }
    
    return headers;
  };

  // Function to check auth status
  const checkAuthStatus = async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/auth/status`);
      setAuthStatus(response.data);
    } catch (error) {
      console.warn('Could not check auth status:', error);
      setAuthStatus(null);
    }
  };

  // Function to save API key
  const saveApiKey = () => {
    setApiKey(apiKeyTemp);
    checkAuthStatus(); // Check status after saving
  };

  // Function to clear API key
  const clearApiKey = () => {
    setApiKey('');
    setApiKeyTemp('');
    localStorage.removeItem('webork-api-key');
    setAuthStatus(null);
  };

  // Check auth status on mount and when API key changes
  useEffect(() => {
    checkAuthStatus();
  }, [apiKey]);

  // Task Queue Monitoring
  const fetchTaskQueueStatus = async (loadMore = false) => {
    // Prevent multiple simultaneous requests
    if (taskQueueLoading) return;
    
    setTaskQueueLoading(true);
    setTaskQueueError(null);
    try {
      const response = await axios.get(`${apiUrl}/api/routines/tasks/status`, {
        timeout: 10000, // 10 second timeout
        headers: {
          ...getHeaders(),
          'Cache-Control': 'no-cache'
        }
      });
      if (response.data && response.data.success) {
        // Transform the data structure to make it easier to work with
        const data = response.data.data;
        
        // Safety checks for data structure
        const summary = data.summary || {};
        const queue = summary.queue || {};
        const summaryData = summary.summary || {};
        const localWorkers = summary.local_workers || {};
        const tasks = data.tasks || {};
        
        const completedTasks = (tasks.completed || []).map(task => {
          // Check if the routine actually succeeded based on the result
          const routineSuccess = task.result?.data?.success || task.result?.result?.success;
          
          // If the task is completed but the routine failed, mark it as failed
          if (routineSuccess === false) {
            return { ...task, status: 'routine_failed', originalStatus: 'completed' };
          }
          
          return { ...task, status: 'completed' };
        });
        const failedTasks = (tasks.failed || []).map(task => ({ ...task, status: 'failed' }));
        const cancelledTasks = (tasks.cancelled || []).map(task => ({ ...task, status: 'cancelled' }));
        
        const transformedData = {
          summary: {
            pending: queue.pending_tasks || 0,
            running: queue.running_tasks || 0,
            completed: queue.completed_tasks || 0,
            failed: queue.failed_tasks || 0
          },
          workers: {
            // Only use local workers data for the dashboard
            active: localWorkers.total_local_workers || 0,
            idle: localWorkers.idle_local_workers || 0,
            busy: localWorkers.busy_local_workers || 0,
            error: localWorkers.error_local_workers || 0,
            max: localWorkers.max_workers || 10, // Use dynamic max_workers from server config
          },
          tasks: [
            ...(tasks.pending || []).map(task => ({ ...task, status: 'pending' })),
            ...(tasks.running || []).map(task => ({ ...task, status: 'running' })),
            ...completedTasks,
            ...failedTasks,
            ...cancelledTasks
          ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)), // Sort by created_at desc
          workers_list: data.workers || []
        };
        
        setTaskQueueStatus(transformedData);
        
        // Initialize pagination state based on the number of completed/failed/cancelled tasks returned
        const totalHistoricalTasksReceived = completedTasks.length + failedTasks.length + cancelledTasks.length;
        const totalHistoricalTasksInSystem = (queue.completed_tasks || 0) + (queue.failed_tasks || 0) + (queue.cancelled_tasks || 0);
        
        // If we received fewer historical tasks than exist in the system, there are more to load
        const hasMoreTasks = totalHistoricalTasksReceived < totalHistoricalTasksInSystem;
        
        setTasksPagination(prev => ({
          ...prev,
          completed: { 
            ...prev.completed, 
            hasMore: hasMoreTasks,
            offset: totalHistoricalTasksReceived
          }
        }));
        
        // Initialize allTasks if it's the first load
        if (!loadMore && allTasks.length === 0) {
          setAllTasks([...completedTasks, ...failedTasks, ...cancelledTasks]);
        }
        
      } else {
        console.warn('Task queue status request succeeded but returned no data');
        // Don't show error to user for empty data
      }
    } catch (error) {
      // Never show network errors to users - only log them
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error fetching task queue status (suppressed):', error);
      }
      // Only show error to user for server errors (non-network issues)
      if (!error._suppressUserError && error.response?.status >= 500) {
        setTaskQueueError('Server issue - venter på gjenoppretting...');
      }
    } finally {
      setTaskQueueLoading(false);
    }
  };

  const cancelTask = async (taskId) => {
    try {
      await axios.post(`${apiUrl}/api/routines/tasks/${taskId}/cancel`, {}, { 
        timeout: 5000,
        headers: getHeaders()
      });
      fetchTaskQueueStatus(); // Refresh status after cancellation
    } catch (error) {
      // Never show network errors to users
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error cancelling task (suppressed):', error);
      }
      // Only show user-friendly error messages for specific server responses
      if (!error._suppressUserError) {
        if (error.response?.status === 404) {
          // Show minimal message for 404
          alert('Task kunne ikke kanselleres');
        } else if (error.response?.status >= 500) {
          alert('Server error - prøv igjen senere');
        }
        // All other errors (including network) are silently handled
      }
    }
  };

  const loadMoreTasks = async (isAutoLoad = false) => {
    if (taskQueueLoading || !tasksPagination.completed.hasMore) return;
    
    // Check if this is an auto-load and we've exceeded the limit
    if (isAutoLoad && autoLoadCount >= 5) {
      setShowLoadMoreButton(true);
      return;
    }
    
    // Set loading state for pagination
    setTasksPagination(prev => ({
      ...prev,
      completed: { ...prev.completed, loading: true }
    }));
    
    try {
      const currentOffset = tasksPagination.completed.offset;
      
      const params = new URLSearchParams({
        limit: '20',
        offset: currentOffset.toString(),
        load_more: 'true'
      });
      
      const response = await axios.get(`${apiUrl}/api/routines/tasks/status?${params}`, {
        timeout: 8000,
        headers: {
          ...getHeaders(),
          'Cache-Control': 'no-cache'
        }
      });
      
      if (response.data && response.data.success) {
        const data = response.data.data;
        const tasks = data.tasks || {};
        
        const newCompletedTasks = (tasks.completed || []).map(task => {
          // Check if the routine actually succeeded based on the result
          const routineSuccess = task.result?.data?.success || task.result?.result?.success;
          
          // If the task is completed but the routine failed, mark it as failed
          if (routineSuccess === false) {
            return { ...task, status: 'routine_failed', originalStatus: 'completed' };
          }
          
          return { ...task, status: 'completed' };
        });
        const newFailedTasks = (tasks.failed || []).map(task => ({ ...task, status: 'failed' }));
        const newCancelledTasks = (tasks.cancelled || []).map(task => ({ ...task, status: 'cancelled' }));
        
        const allNewTasks = [...newCompletedTasks, ...newFailedTasks, ...newCancelledTasks];
        
        if (allNewTasks.length === 0) {
          // No more tasks to load
          setTasksPagination(prev => ({
            ...prev,
            completed: { ...prev.completed, hasMore: false, loading: false }
          }));
        } else {
          // Add new tasks to allTasks state
          setAllTasks(prev => [...prev, ...allNewTasks]);
          
          // Update task queue status to include new tasks
          setTaskQueueStatus(prevStatus => ({
            ...prevStatus,
            tasks: [...prevStatus.tasks, ...allNewTasks].sort((a, b) => 
              new Date(b.created_at) - new Date(a.created_at)
            )
          }));
          
          // Update pagination state
          setTasksPagination(prev => ({
            ...prev,
            completed: { 
              ...prev.completed, 
              offset: currentOffset + allNewTasks.length,
              loading: false,
              hasMore: allNewTasks.length === 20 // If we got fewer than requested, no more to load
            }
          }));
          
          // Update auto-load counter and button visibility
          if (isAutoLoad) {
            setAutoLoadCount(prev => prev + 1);
            if (autoLoadCount + 1 >= 5) {
              setShowLoadMoreButton(true);
            }
          } else {
            // Manual load via button - reset counter for another 5 auto-loads
            setAutoLoadCount(0);
            setShowLoadMoreButton(false);
          }
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error loading more tasks (suppressed):', error);
      }
      
      // Reset loading state on error
      setTasksPagination(prev => ({
        ...prev,
        completed: { ...prev.completed, loading: false }
      }));
    }
  };

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefresh && tabValue === 2) { // Task Queue tab
      const interval = setInterval(fetchTaskQueueStatus, 5000); // Refresh every 5 seconds (reduced from 2)
      return () => clearInterval(interval);
    }
  }, [autoRefresh, tabValue]);

  // Initial fetch when switching to Task Queue tab
  useEffect(() => {
    if (tabValue === 2) {
      // Reset infinite scroll state when entering Task Queue tab
      setAutoLoadCount(0);
      setShowLoadMoreButton(false);
      fetchTaskQueueStatus();
      fetchRecoveryData(); // Also fetch recovery data
    }
    if (tabValue === 3) { // Sessions tab
      fetchSessions();
    }
  }, [tabValue]);

  // Scroll detection for automatic infinite scrolling
  useEffect(() => {
    if (tabValue !== 2) return; // Only on Task Queue tab
    
    const handleScroll = () => {
      // Check if we're at the bottom of the page
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Trigger when user is within 100px of the bottom
      const isNearBottom = scrollTop + windowHeight >= documentHeight - 100;
      
      if (isNearBottom && 
          tasksPagination.completed.hasMore && 
          !tasksPagination.completed.loading && 
          !taskQueueLoading &&
          autoLoadCount < 5 &&
          !showLoadMoreButton) {
        console.log('Auto-loading more tasks on scroll (count:', autoLoadCount + 1, ')');
        loadMoreTasks(true); // true = isAutoLoad
      }
    };
    
    // Add scroll listener with throttling
    let scrollTimer = null;
    const throttledScroll = () => {
      if (scrollTimer) return;
      scrollTimer = setTimeout(() => {
        handleScroll();
        scrollTimer = null;
      }, 200); // Throttle to every 200ms
    };
    
    window.addEventListener('scroll', throttledScroll);
    
    return () => {
      window.removeEventListener('scroll', throttledScroll);
      if (scrollTimer) clearTimeout(scrollTimer);
    };
  }, [tabValue, tasksPagination.completed.hasMore, tasksPagination.completed.loading, taskQueueLoading, autoLoadCount, showLoadMoreButton]);

  // Session monitoring
  const fetchSessions = async () => {
    // Prevent multiple simultaneous fetches
    if (sessionsLoading) return;
    
    setSessionsLoading(true);
    setSessionsError(null);
    try {
      const response = await axios.get(`${apiUrl}/api/sessions/`, {
        timeout: 8000, // 8 second timeout
        headers: {
          ...getHeaders(),
          'Cache-Control': 'no-cache'
        }
      });
      if (response.data && response.data.success) {
        const sessionsList = response.data.data.sessions || [];
        setSessions(sessionsList);
        
        // Only load screenshots if we have sessions and user is viewing sessions tab
        if (sessionsList.length > 0 && tabValue === 3) {
          // Load initial screenshots for all sessions with individual timeout and error handling
          const screenshotPromises = sessionsList.map(async (session) => {
            try {
              const screenshotResponse = await axios.get(`${apiUrl}/api/sessions/${session.session_id}/live-screenshot`, {
                timeout: 5000, // Shorter timeout for screenshots
                headers: getHeaders()
              });
              if (screenshotResponse.data && screenshotResponse.data.success) {
                return {
                  ...session,
                  screenshot: screenshotResponse.data.data.screenshot,
                  url: screenshotResponse.data.data.url,
                  title: screenshotResponse.data.data.title
                };
              }
            } catch (error) {
              // Silently handle screenshot loading errors
              // Network errors for screenshots are expected and common
            }
            return session; // Return original session if screenshot loading fails
          });
          
          // Wait for all screenshot requests to complete (with timeout)
          const sessionsWithScreenshots = await Promise.allSettled(screenshotPromises);
          
          // Update sessions with screenshots that loaded successfully
          const updatedSessions = sessionsWithScreenshots.map((result, index) => 
            result.status === 'fulfilled' ? result.value : sessionsList[index]
          );
          setSessions(updatedSessions);
        }
        
        // Set up streams or polling for new sessions based on preference
        sessionsList.forEach(session => {
          if (!sessionStreams[session.session_id]) {
            if (useStreaming) {
              setupSessionStream(session.session_id);
            } else {
              setupSessionPolling(session.session_id);
            }
          }
        });
        
        // Clean up streams for sessions that no longer exist
        Object.keys(sessionStreams).forEach(sessionId => {
          if (!sessionsList.some(s => s.session_id === sessionId)) {
            cleanupSessionStream(sessionId);
          }
        });
      } else {
        console.warn('Sessions request succeeded but returned no data');
        // Don't show error to user for empty data
        setSessions([]);
      }
    } catch (error) {
      // Never show network errors to users
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error fetching sessions (suppressed):', error);
      }
      // Only show error to user for server errors (non-network issues)
      if (!error._suppressUserError && error.response?.status >= 500) {
        setSessionsError('Server issue - venter på gjenoppretting...');
      }
    } finally {
      setSessionsLoading(false);
    }
  };

  const setupSessionStream = (sessionId) => {
    if (sessionStreams[sessionId]) return; // Already exists
    
    let eventSource;
    let retryCount = 0;
    const maxRetries = 5;
    
    const connectStream = () => {
      eventSource = new EventSource(`${apiUrl}/api/sessions/${sessionId}/stream`);
      
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Handle different message types
          if (data.type === 'session_update' && data.screenshot) {
            setSessions(prevSessions => 
              prevSessions.map(session => 
                session.session_id === sessionId 
                  ? { ...session, screenshot: data.screenshot, url: data.url, title: data.title, last_update: new Date() }
                  : session
              )
            );
          } else if (data.type === 'stream_ended' && data.should_reconnect && retryCount < maxRetries) {
            // Auto-reconnect for continuous streaming
            console.log(`Stream ended for session ${sessionId}, reconnecting...`);
            setTimeout(() => {
              retryCount++;
              connectStream();
            }, 1000);
          } else if (data.type === 'session_ended') {
            // Session actually ended, clean up
            cleanupSessionStream(sessionId);
          }
        } catch (error) {
          console.error('Error parsing session stream data:', error);
        }
      };
      
      eventSource.onerror = (error) => {
        console.error('Session stream error:', error);
        eventSource.close();
        
        // Fallback to polling if streaming fails
        if (retryCount >= maxRetries) {
          console.log(`Max retries reached for session ${sessionId}, switching to polling`);
          setupSessionPolling(sessionId);
        } else {
          setTimeout(() => {
            retryCount++;
            connectStream();
          }, 2000);
        }
      };
      
      return eventSource;
    };
    
    const stream = connectStream();
    setSessionStreams(prev => ({ ...prev, [sessionId]: stream }));
  };

  const setupSessionPolling = (sessionId) => {
    // Clean up any existing stream first
    cleanupSessionStream(sessionId);
    
    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/sessions/${sessionId}/live-screenshot`, {
          headers: getHeaders()
        });
        if (response.data && response.data.success) {
          const data = response.data.data;
          setSessions(prevSessions => 
            prevSessions.map(session => 
              session.session_id === sessionId 
                ? { 
                    ...session, 
                    screenshot: data.screenshot, 
                    url: data.url, 
                    title: data.title, 
                    last_update: new Date() 
                  }
                : session
            )
          );
        } else if (!response.data.data.session_exists) {
          // Session no longer exists, stop polling
          clearInterval(pollInterval);
        }
      } catch (error) {
        console.error(`Error polling session ${sessionId}:`, error);
        // If session is not found, stop polling
        if (error.response?.status === 404) {
          clearInterval(pollInterval);
        }
      }
    }, 2000); // Poll every 2 seconds
    
    // Store interval for cleanup
    setSessionStreams(prev => ({ ...prev, [sessionId]: { close: () => clearInterval(pollInterval) } }));
  };

  const cleanupSessionStream = (sessionId) => {
    if (sessionStreams[sessionId]) {
      sessionStreams[sessionId].close();
      setSessionStreams(prev => {
        const updated = { ...prev };
        delete updated[sessionId];
        return updated;
      });
    }
  };

  // Switch streaming method when toggle changes
  useEffect(() => {
    // Clean up existing streams/polling
    Object.keys(sessionStreams).forEach(sessionId => {
      cleanupSessionStream(sessionId);
    });
    
    // Set up new method for all sessions
    sessions.forEach(session => {
      if (useStreaming) {
        setupSessionStream(session.session_id);
      } else {
        setupSessionPolling(session.session_id);
      }
    });
  }, [useStreaming]); // React when streaming preference changes

  // Auto-refresh sessions (for polling mode)
  useEffect(() => {
    if (sessionsAutoRefresh && !useStreaming && tabValue === 3) { // Sessions tab with polling
      const interval = setInterval(fetchSessions, 10000); // Refresh every 10 seconds (reduced from 5)
      return () => clearInterval(interval);
    }
  }, [sessionsAutoRefresh, useStreaming, tabValue]);

  // Cleanup streams when component unmounts
  useEffect(() => {
    return () => {
      Object.values(sessionStreams).forEach(stream => stream.close());
      // Cleanup routine polling intervals
      Object.values(routinePollingIntervals).forEach(({ pollInterval, timeoutId }) => {
        clearInterval(pollInterval);
        clearTimeout(timeoutId);
      });
    };
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleRoutineClick = (routine) => {
    setSelectedRoutine(routine);
    setRoutineViewerOpen(true);
  };

  const handleRoutineExecute = (routineName, result) => {
    // Handle successful execution if needed
    console.log(`Routine ${routineName} executed successfully:`, result);
  };

  const runRoutine = async (routineName) => {
    setRunStatus(s => ({ ...s, [routineName]: { loading: true, result: null, error: null } }));
    try {
      // Build parameters based on routine type
      let params = {};
      
      if (routineName === 'test_form_routine') {
        // For test form routine, provide default parameters if none are specified
        params = {
          field1: 'Default Value 1',
          field2: 'Default Value 2', 
          field3: 'Default Value 3',
          field4: 'Default Value 4'
        };
      }
      
      const res = await axios.post(`${apiUrl}/api/routines/${routineName}/execute`, { params }, {
        headers: getHeaders()
      });
      
      if (res.data.success && res.data.data?.task_id) {
        const taskId = res.data.data.task_id;
        // Poll for task completion
        pollTaskStatus(routineName, taskId);
      } else {
        setRunStatus(s => ({ ...s, [routineName]: { loading: false, result: res.data, error: null } }));
      }
    } catch (e) {
      setRunStatus(s => ({ ...s, [routineName]: { loading: false, result: null, error: e.response?.data?.error || e.message } }));
    }
  };

  const pollTaskStatus = async (routineName, taskId) => {
    // Clear any existing polling for this routine
    if (routinePollingIntervals[routineName]) {
      clearInterval(routinePollingIntervals[routineName].pollInterval);
      clearTimeout(routinePollingIntervals[routineName].timeoutId);
    }

    let pollCount = 0;
    const maxPolls = 150; // 5 minutes with 2 second intervals

    const pollInterval = setInterval(async () => {
      try {
        pollCount++;
        // Only log polling in development mode
        if (process.env.NODE_ENV === 'development') {
          console.log(`Polling task ${taskId} status for routine ${routineName} (attempt ${pollCount})`);
        }
        
        const response = await axios.get(`${apiUrl}/api/routines/tasks/${taskId}/status`, {
          headers: getHeaders()
        });
        
        if (response.data.success) {
          const taskData = response.data.data;
          const status = taskData.status;
          
          // Only log in development mode
          if (process.env.NODE_ENV === 'development') {
            console.log(`Task ${taskId} status: ${status}`);
          }
          
          // If task is completed, failed, or cancelled - stop polling
          if (['completed', 'failed', 'cancelled'].includes(status)) {
            clearInterval(pollInterval);
            clearTimeout(timeoutId);
            
            // Clean up polling intervals
            setRoutinePollingIntervals(prev => {
              const newState = { ...prev };
              delete newState[routineName];
              return newState;
            });
            
            // Update status based on result
            if (status === 'completed') {
              setRunStatus(s => ({ 
                ...s, 
                [routineName]: { 
                  loading: false, 
                  result: taskData.result || { success: true, message: 'Task completed successfully' }, 
                  error: null 
                } 
              }));
              console.log(`Task ${taskId} completed successfully`);
            } else if (status === 'failed') {
              setRunStatus(s => ({ 
                ...s, 
                [routineName]: { 
                  loading: false, 
                  result: null, 
                  error: taskData.error_message || taskData.error || 'Task failed' 
                } 
              }));
              if (process.env.NODE_ENV === 'development') {
                console.log(`Task ${taskId} failed:`, taskData.error_message || taskData.error);
              }
            } else if (status === 'cancelled') {
              setRunStatus(s => ({ 
                ...s, 
                [routineName]: { 
                  loading: false, 
                  result: null, 
                  error: 'Task was cancelled' 
                } 
              }));
              console.log(`Task ${taskId} was cancelled`);
            }
          } else if (pollCount >= maxPolls) {
            // Timeout after max polls
            clearInterval(pollInterval);
            clearTimeout(timeoutId);
            setRoutinePollingIntervals(prev => {
              const newState = { ...prev };
              delete newState[routineName];
              return newState;
            });
            setRunStatus(s => ({ 
              ...s, 
              [routineName]: { 
                loading: false, 
                result: null, 
                error: 'Task timeout - took longer than 5 minutes' 
              } 
            }));
            if (process.env.NODE_ENV === 'development') {
              console.log(`Task ${taskId} timed out after ${maxPolls} polling attempts`);
            }
          }
          // If status is 'pending' or 'running', continue polling
        }
      } catch (error) {
        // Suppress network errors from polling
        if (process.env.NODE_ENV === 'development') {
          console.warn('Error polling task status (suppressed):', error.message);
        }
        
        // If we get 404 or the task doesn't exist anymore, consider it failed
        if (error.response?.status === 404) {
          clearInterval(pollInterval);
          clearTimeout(timeoutId);
          setRoutinePollingIntervals(prev => {
            const newState = { ...prev };
            delete newState[routineName];
            return newState;
          });
          setRunStatus(s => ({ 
            ...s, 
            [routineName]: { 
              loading: false, 
              result: null, 
              error: 'Task not found - may have been removed' 
            } 
          }));
        }
        // For other errors, continue polling unless we've reached max attempts
        else if (pollCount >= maxPolls) {
          clearInterval(pollInterval);
          clearTimeout(timeoutId);
          setRoutinePollingIntervals(prev => {
            const newState = { ...prev };
            delete newState[routineName];
            return newState;
          });
          setRunStatus(s => ({ 
            ...s, 
            [routineName]: { 
              loading: false, 
              result: null, 
              error: 'Failed to get task status: ' + error.message 
            } 
          }));
        }
      }
    }, 2000); // Poll every 2 seconds

    // Set a timeout to stop polling after 5 minutes (backup)
    const timeoutId = setTimeout(() => {
      clearInterval(pollInterval);
      setRoutinePollingIntervals(prev => {
        const newState = { ...prev };
        delete newState[routineName];
        return newState;
      });
      setRunStatus(s => ({ 
        ...s, 
        [routineName]: { 
          loading: false, 
          result: null, 
          error: 'Task timeout - took longer than 5 minutes' 
        } 
      }));        console.log(`Task ${taskId} timed out (backup timeout)`);
    }, 300000); // 5 minutes

    // Store interval and timeout references for cleanup
    setRoutinePollingIntervals(prev => ({
      ...prev,
      [routineName]: { pollInterval, timeoutId }
    }));
  };

  // --- Standard API test funksjon ---
  const callEndpoint = async (endpoint) => {
    const url = apiUrl + endpoint.path;
    try {
      let res;
      if (endpoint.method === 'GET') {
        res = await axios.get(url, { 
          timeout: 8000,
          headers: getHeaders()
        });
      } else {
        res = await axios.post(url, endpoint.body ? JSON.parse(endpoint.body) : {}, { 
          timeout: 8000,
          headers: getHeaders()
        });
      }
      setResults(r => ({ ...r, [endpoint.label]: res.data }));
    } catch (e) {
      // For API testing, show results but don't spam console
      if (process.env.NODE_ENV === 'development') {
        console.warn(`API test failed for ${endpoint.label} (expected in testing):`, e.message);
      }
      const errorMessage = e.response?.data?.error || e.message || 'Request failed';
      setResults(r => ({ ...r, [endpoint.label]: { error: errorMessage, status: e.response?.status } }));
    }
  };

  const callCustom = async () => {
    const url = apiUrl + customPath;
    try {
      let res;
      if (customMethod === 'GET') {
        res = await axios.get(url, { 
          timeout: 8000,
          headers: getHeaders()
        });
      } else {
        res = await axios.post(url, customBody ? JSON.parse(customBody) : {}, { 
          timeout: 8000,
          headers: getHeaders()
        });
      }
      setResults(r => ({ ...r, 'Custom': res.data }));
    } catch (e) {
      // For API testing, show results but don't spam console
      if (process.env.NODE_ENV === 'development') {
        console.warn('Custom API test failed (expected in testing):', e.message);
      }
      const errorMessage = e.response?.data?.error || e.message || 'Request failed';
      setResults(r => ({ ...r, 'Custom': { error: errorMessage, status: e.response?.status } }));
    }
  };

  const fetchRecoveryData = async () => {
    if (recoveryLoading) return;
    
    setRecoveryLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/api/health/recovery/details`, {
        timeout: 8000,
        headers: {
          ...getHeaders(),
          'Cache-Control': 'no-cache'
        }
      });
      
      if (response.data) {
        setRecoveryData(response.data);
      }
    } catch (error) {
      // Silently handle recovery data errors - not critical for main functionality
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error fetching recovery data (suppressed):', error);
      }
    } finally {
      setRecoveryLoading(false);
    }
  };

  return (
    <NetworkErrorSuppressor>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>WebOrk Dashboard</Typography>
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Rutiner" />
            <Tab label="API Test" />
            <Tab label="Task Queue" />
            <Tab label="Sessions" />
            <Tab label="API-Key" />
            <Tab label="Avansert" />
          </Tabs>
        </Box>

        {/* ...existing content... */}
        {/* Routines Tab */}
        {tabValue === 0 && (
          <RoutineManager 
            onRoutineClick={handleRoutineClick} 
            apiKey={apiKey}
            getHeaders={getHeaders}
          />
        )}

        {/* API Test Tab */}
        {tabValue === 1 && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Test standard endepunkter</Typography>
            {endpoints.map(ep => (
              <Box key={ep.label} sx={{ mb: 2 }}>
                <Button variant="contained" onClick={() => callEndpoint(ep)} sx={{ mr: 2 }}>
                  {ep.label}
                </Button>
                {results[ep.label] && (
                  <pre style={{ 
                    background: '#222', 
                    color: '#fff', 
                    padding: 8, 
                    marginTop: 8, 
                    borderRadius: 4, 
                    maxHeight: 200, 
                    overflow: 'auto' 
                  }}>
                    {JSON.stringify(results[ep.label], null, 2)}
                  </pre>
                )}
              </Box>
            ))}
          </Paper>
        )}

      {/* Task Queue Tab */}
      {tabValue === 2 && (
        <Box>
          {/* Control Panel */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Task Queue Monitoring</Typography>
              <Box>
                <Button
                  variant={autoRefresh ? "contained" : "outlined"}
                  color={autoRefresh ? "success" : "primary"}
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  sx={{ mr: 1 }}
                  startIcon={autoRefresh ? <PauseIcon /> : <PlayIcon />}
                >
                  {autoRefresh ? 'Stop Auto-Refresh' : 'Auto-Refresh'}
                </Button>
                <Tooltip title="Refresh Now">
                  <IconButton 
                    onClick={fetchTaskQueueStatus} 
                    disabled={taskQueueLoading}
                    color="primary"
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {taskQueueError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {taskQueueError}
              </Alert>
            )}
          </Paper>

          {/* Queue Statistics */}
          {taskQueueStatus && (
            <>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Badge badgeContent={taskQueueStatus.summary.pending} color="warning">
                        <ScheduleIcon sx={{ fontSize: 40, color: 'orange' }} />
                      </Badge>
                      <Typography variant="h6" sx={{ mt: 1 }}>Pending</Typography>
                      <Typography variant="h4">{taskQueueStatus.summary.pending}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Badge badgeContent={taskQueueStatus.summary.running} color="primary">
                        <PlayIcon sx={{ fontSize: 40, color: 'blue' }} />
                      </Badge>
                      <Typography variant="h6" sx={{ mt: 1 }}>Running</Typography>
                      <Typography variant="h4">{taskQueueStatus.summary.running}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Badge badgeContent={taskQueueStatus.summary.completed} color="success">
                        <CheckCircleIcon sx={{ fontSize: 40, color: 'green' }} />
                      </Badge>
                      <Typography variant="h6" sx={{ mt: 1 }}>Completed</Typography>
                      <Typography variant="h4">{taskQueueStatus.summary.completed}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Badge badgeContent={taskQueueStatus.summary.failed} color="error">
                        <ErrorIcon sx={{ fontSize: 40, color: 'red' }} />
                      </Badge>
                      <Typography variant="h6" sx={{ mt: 1 }}>Failed</Typography>
                      <Typography variant="h4">{taskQueueStatus.summary.failed}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Worker Statistics */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Worker Status</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle2">Total Workers</Typography>
                      <Typography variant="h5" color="primary">
                        {taskQueueStatus.workers.max}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle2">Idle Workers</Typography>
                      <Typography variant="h5" color="success.main">
                        {taskQueueStatus.workers.idle}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle2">Busy Workers</Typography>
                      <Typography variant="h5" color="warning.main">
                        {taskQueueStatus.workers.busy}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Recovery Status */}
              {recoveryData && recoveryData.total_recovered > 0 && (
                <Paper sx={{ p: 2, mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" gutterBottom>Recovery Status</Typography>
                    <Button size="small" onClick={fetchRecoveryData} disabled={recoveryLoading}>
                      <RefreshIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      Refresh
                    </Button>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="subtitle2">Total Recovered</Typography>
                        <Typography variant="h5" color="warning.main">
                          {recoveryData.total_recovered}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="subtitle2">Currently Pending</Typography>
                        <Typography variant="h5" color="info.main">
                          {recoveryData.current_recovery?.pending_tasks || 0}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="subtitle2">Completed</Typography>
                        <Typography variant="h5" color="success.main">
                          {recoveryData.current_recovery?.completed_tasks || 0}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="subtitle2">Recovery Active</Typography>
                        <Chip
                          label={recoveryData.current_recovery?.recovery_in_progress ? 'Yes' : 'No'}
                          color={recoveryData.current_recovery?.recovery_in_progress ? 'warning' : 'success'}
                          size="small"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  {recoveryData.recovery_stats && recoveryData.recovery_stats.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Recent Recovery Events:</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {recoveryData.recovery_stats.slice(0, 3).map((stat, index) => (
                          <Chip
                            key={index}
                            label={`${stat.reason}: ${stat.count} tasks`}
                            variant="outlined"
                            size="small"
                            color="warning"
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Paper>
              )}

              {/* Task List */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Recent Tasks</Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Task ID</TableCell>
                        <TableCell>Routine</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Updated</TableCell>
                        <TableCell>Progress</TableCell>
                        <TableCell>Recovery</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {taskQueueStatus && taskQueueStatus.tasks && Array.isArray(taskQueueStatus.tasks) && taskQueueStatus.tasks.map((task) => (
                        <TableRow key={task.id}>
                          <TableCell>{task.id}</TableCell>
                          <TableCell>{task.config?.routine_name || 'Unknown'}</TableCell>
                          <TableCell>
                            <Chip
                              label={task.status === 'routine_failed' ? 'Routine Failed' : task.status}
                              color={
                                task.status === 'completed' ? 'success' :
                                task.status === 'failed' || task.status === 'routine_failed' ? 'error' :
                                task.status === 'running' ? 'primary' :
                                'default'
                              }
                              size="small"
                              icon={
                                task.status === 'completed' ? <CheckCircleIcon /> :
                                task.status === 'failed' || task.status === 'routine_failed' ? <ErrorIcon /> :
                                task.status === 'running' ? <PlayIcon /> :
                                <ScheduleIcon />
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {task.created_at ? new Date(task.created_at).toLocaleString() : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {task.updated_at ? new Date(task.updated_at).toLocaleString() : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {task.status === 'running' && task.progress !== null ? (
                              <LinearProgress 
                                variant="determinate" 
                                value={task.progress} 
                                sx={{ width: 100 }} 
                              />
                            ) : (
                              <LinearProgress 
                                variant="determinate" 
                                value={0} 
                                sx={{ width: 100 }} 
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {task.is_recovered ? (
                              <Tooltip title={`Restarted due to: ${task.recovery_reason || 'server restart'}`}>
                                <Chip
                                  label="Restarted"
                                  color="warning"
                                  size="small"
                                  variant="outlined"
                                  icon={<RefreshIcon />}
                                />
                              </Tooltip>
                            ) : (
                              <span>-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {(task.status === 'pending' || task.status === 'running') && (
                              <Tooltip title="Cancel Task">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => cancelTask(task.id)}
                                >
                                  <StopIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!taskQueueStatus || !taskQueueStatus.tasks || !Array.isArray(taskQueueStatus.tasks) || taskQueueStatus.tasks.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ textAlign: 'center', py: 3 }}>
                            <Typography color="text.secondary">No tasks found</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* Load More Tasks Button */}
                {taskQueueStatus && taskQueueStatus.tasks && taskQueueStatus.tasks.length > 0 && tasksPagination.completed.hasMore && showLoadMoreButton && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={() => loadMoreTasks(false)}
                      disabled={taskQueueLoading || tasksPagination.completed.loading}
                      startIcon={tasksPagination.completed.loading ? <CircularProgress size={16} /> : null}
                      sx={{ px: 3, py: 1 }}
                    >
                      {tasksPagination.completed.loading ? 'Loading...' : 'Load More Tasks'}
                    </Button>
                  </Box>
                )}
              </Paper>
            </>
          )}
        </Box>
      )}

      {/* Sessions Tab */}
      {tabValue === 3 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" gutterBottom>Aktive Browser Sesjoner</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">Streaming:</Typography>
              <Button
                size="small"
                variant={useStreaming ? "contained" : "outlined"}
                color={useStreaming ? "primary" : "secondary"}
                onClick={() => setUseStreaming(!useStreaming)}
                sx={{ minWidth: 80 }}
              >
                {useStreaming ? 'ON' : 'OFF'}
              </Button>
              
              {!useStreaming && (
                <Button
                  variant={sessionsAutoRefresh ? "contained" : "outlined"}
                  color={sessionsAutoRefresh ? "success" : "primary"}
                  onClick={() => setSessionsAutoRefresh(!sessionsAutoRefresh)}
                  sx={{ mr: 1 }}
                  startIcon={sessionsAutoRefresh ? <PauseIcon /> : <PlayIcon />}
                >
                  {sessionsAutoRefresh ? 'Stop Polling' : 'Auto-Poll'}
                </Button>
              )}
              
              <Tooltip title="Refresh Now">
                <IconButton 
                  onClick={fetchSessions}
                  disabled={sessionsLoading}
                  color="primary"
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          {sessionsLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          )}
          
          {sessionsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {sessionsError}
            </Alert>
          )}
          
          {sessions.length === 0 && !sessionsLoading && !sessionsError && (
            <Alert severity="info">
              Ingen aktive sesjoner funnet. Kjør en rutine for å se browser sesjoner.
            </Alert>
          )}
          
          {sessions.length > 0 && (
            <Grid container spacing={3}>
              {sessions.map((session) => (
                <Grid item xs={12} md={6} lg={4} key={session.session_id}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {session.routine_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Worker: {session.worker_id?.substring(0, 8)}...
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Task: {session.task_id?.substring(0, 8)}...
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Startet: {new Date(session.started_at).toLocaleTimeString()}
                      </Typography>
                      
                      {/* Screenshot placeholder */}
                      <Box sx={{ 
                        mt: 2, 
                        border: '1px solid #ddd', 
                        borderRadius: 1, 
                        height: 200, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5'
                      }}>
                        {session.screenshot ? (
                          <img 
                            src={`data:image/png;base64,${session.screenshot}`}
                            alt="Browser screenshot"
                            style={{ 
                              maxWidth: '100%', 
                              maxHeight: '100%', 
                              objectFit: 'contain' 
                            }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Laster skjermbilde...
                          </Typography>
                        )}
                      </Box>
                      
                      {/* URL info */}
                      {session.url && session.url !== 'unknown' && (
                        <Box sx={{ 
                          mt: 1, 
                          p: 1, 
                          backgroundColor: 'rgba(0,0,0,0.05)', 
                          borderRadius: 1 
                        }}>
                          <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                            {session.url}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      )}

      {/* API-Key Tab */}
      {tabValue === 4 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>API-Nøkkel Konfigurasjon</Typography>
          
          {/* Authentication Status */}
          {authStatus && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>Autentiseringsstatus</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Chip 
                  label={authStatus.api_key_required ? "API-nøkkel påkrevd" : "API-nøkkel ikke påkrevd"} 
                  color={authStatus.api_key_required ? "error" : "success"}
                />
                <Chip 
                  label={authStatus.api_key_configured ? "API-nøkkel konfigurert" : "API-nøkkel ikke konfigurert"} 
                  color={authStatus.api_key_configured ? "success" : "warning"}
                />
              </Box>
              
              {authStatus.auth_header_formats && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Støttede header-formater:</Typography>
                  <ul>
                    {authStatus.auth_header_formats.map((format, index) => (
                      <li key={index}><code>{format}</code></li>
                    ))}
                  </ul>
                </Box>
              )}
            </Box>
          )}

          {/* API Key Input */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>API-Nøkkel</Typography>
            <TextField
              label="WebOrk API-Nøkkel"
              value={apiKeyTemp}
              onChange={(e) => setApiKeyTemp(e.target.value)}
              placeholder="Skriv inn din WebOrk API-nøkkel her"
              fullWidth
              type="password"
              sx={{ mb: 2 }}
              helperText="API-nøkkelen lagres i localStorage og brukes for alle API-kall"
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                variant="contained" 
                onClick={saveApiKey}
                color="primary"
              >
                Lagre API-Nøkkel
              </Button>
              
              <Button 
                variant="outlined" 
                onClick={clearApiKey}
                color="secondary"
              >
                Fjern API-Nøkkel
              </Button>
              
              <Button 
                variant="outlined" 
                onClick={checkAuthStatus}
                color="info"
              >
                Sjekk Status
              </Button>
            </Box>
          </Box>

          {/* Current Status */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>Nåværende konfigurasjon</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip 
                label={apiKey ? "API-nøkkel er satt" : "Ingen API-nøkkel"} 
                color={apiKey ? "success" : "default"}
              />
              {apiKey && (
                <Chip 
                  label={`Nøkkel: ${apiKey.substring(0, 8)}...`} 
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          {/* Usage Instructions */}
          <Box>
            <Typography variant="h6" gutterBottom>Bruksinstruksjoner</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              1. Få API-nøkkelen fra din WebOrk server administrator<br/>
              2. Skriv inn nøkkelen i feltet ovenfor<br/>
              3. Klikk "Lagre API-Nøkkel" for å aktivere autentisering<br/>
              4. Alle API-kall vil nå inkludere X-API-Key header automatisk
            </Typography>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>Merk:</strong> API-nøkkelen lagres lokalt i nettleseren din og sendes med alle API-forespørsler. 
              Del aldri din API-nøkkel med andre.
            </Alert>
          </Box>
        </Paper>
      )}

      {/* Advanced Tab */}
      {tabValue === 5 && (
        <>
          <Paper sx={{ p: 2, mt: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Rutiner (Legacy View)</Typography>
            {routinesLoading && <CircularProgress />}
            {routinesError && <Alert severity="error">{routinesError}</Alert>}
            {!routinesLoading && !routinesError && routines.length === 0 && (
              <Typography>Ingen rutiner funnet.</Typography>
            )}
            {!routinesLoading && !routinesError && routines.map(routine => (
              <Paper key={routine.name} sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6">{routine.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {routine.description || 'Ingen beskrivelse.'}
                </Typography>
                <Button
                  variant="contained"
                  sx={{ mt: 1 }}
                  onClick={() => runRoutine(routine.name)}
                  disabled={runStatus[routine.name]?.loading}
                >
                  {runStatus[routine.name]?.loading ? 'Kjører...' : 'Kjør'}
                </Button>
                {runStatus[routine.name]?.result && (
                  <Alert sx={{ mt: 2 }} severity="success">
                    {typeof runStatus[routine.name].result === 'object'
                      ? JSON.stringify(runStatus[routine.name].result)
                      : runStatus[routine.name].result}
                  </Alert>
                )}
                {runStatus[routine.name]?.error && (
                  <Alert sx={{ mt: 2 }} severity="error">
                    {runStatus[routine.name].error}
                  </Alert>
                )}
              </Paper>
            ))}
          </Paper>
          
          <Divider />
          
          <Paper sx={{ p: 2, mt: 3 }}>
            <Typography variant="h6" gutterBottom>Custom API kall</Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField 
                label="Path" 
                value={customPath} 
                onChange={e => setCustomPath(e.target.value)} 
                size="small" 
              />
              <TextField 
                label="Method" 
                value={customMethod} 
                onChange={e => setCustomMethod(e.target.value)} 
                size="small" 
              />
              <TextField 
                label="Body (JSON)" 
                value={customBody} 
                onChange={e => setCustomBody(e.target.value)} 
                size="small" 
              />
              <Button variant="outlined" onClick={callCustom}>Kjør</Button>
            </Box>
            {results['Custom'] && (
              <pre style={{ 
                background: '#222', 
                color: '#fff', 
                padding: 8, 
                borderRadius: 4, 
                maxHeight: 200, 
                overflow: 'auto' 
              }}>
                {JSON.stringify(results['Custom'], null, 2)}
              </pre>
            )}
          </Paper>
        </>
      )}

      {/* Routine Viewer Dialog */}
      <RoutineViewer
        routine={selectedRoutine}
        open={routineViewerOpen}
        onClose={() => setRoutineViewerOpen(false)}
        onExecute={handleRoutineExecute}
      />
      </Box>
    </NetworkErrorSuppressor>
  );
};

export default WebOrkDashboard;
